import { useEffect, useRef, useState } from "preact/hooks"
import type { MatchState } from "../types"

// useMatchChannel subscribes to GET /api/matches/:id/events (SSE) and keeps
// the latest, per-viewer-redacted MatchState. The browser's native
// EventSource auto-reconnects on drop, so this hook does no reconnect logic
// of its own — it just re-subscribes when `id` changes and tears down the
// connection on unmount.
export function useMatchChannel(id: number): {
	state: MatchState | null
	error: string
	reconnect: () => void
} {
	const [state, setState] = useState<MatchState | null>(null)
	const [error, setError] = useState("")
	// Bumped to force a fresh subscription (and thus a fresh full-state
	// snapshot) even when `id` is unchanged — used to re-sync a stale board
	// after a "not the current question" rejection.
	const [reconnectKey, setReconnectKey] = useState(0)
	// Reset stale state immediately when the id changes, so a MatchPlay
	// remount (e.g. "play again" onto a new match id) never briefly renders
	// the previous match's data under the new id.
	const lastID = useRef(id)
	if (lastID.current !== id) {
		lastID.current = id
		if (state !== null) setState(null)
	}

	useEffect(() => {
		const es = new EventSource(`/api/matches/${id}/events`)
		es.addEventListener("state", (e) => {
			setError("")
			setState(JSON.parse((e as MessageEvent).data) as MatchState)
		})
		es.onerror = () => {
			// EventSource retries on its own; this just surfaces a transient
			// "connection lost" hint while it does.
			setError("Connection lost, reconnecting…")
		}
		return () => es.close()
	}, [id, reconnectKey])

	return {
		state,
		error,
		reconnect: () => setReconnectKey((k) => k + 1),
	}
}
